import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, CloudOff, RefreshCw, Check } from 'lucide-react';
import { getSyncQueueCount } from '../utils/offlineDB';
import { syncPendingOperations, setupOfflineListeners } from '../utils/offlineApi';

const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check pending operations count
    const checkPending = async () => {
      const count = await getSyncQueueCount();
      setPendingCount(count);
    };
    checkPending();

    // Setup online/offline listeners
    const cleanup = setupOfflineListeners(
      // onOnline
      () => {
        setIsOnline(true);
        setShowBanner(true);
        setTimeout(() => setShowBanner(false), 5000);
      },
      // onOffline
      () => {
        setIsOnline(false);
        setShowBanner(true);
      },
      // onSyncComplete
      (result) => {
        setSyncResult(result);
        checkPending();
        setTimeout(() => setSyncResult(null), 3000);
      }
    );

    // Check pending count periodically
    const interval = setInterval(checkPending, 10000);

    return () => {
      cleanup();
      clearInterval(interval);
    };
  }, []);

  const handleManualSync = async () => {
    if (!isOnline || syncing) return;
    
    setSyncing(true);
    const result = await syncPendingOperations();
    setSyncResult(result);
    setSyncing(false);
    
    const count = await getSyncQueueCount();
    setPendingCount(count);
    
    setTimeout(() => setSyncResult(null), 3000);
  };

  // Don't show anything if online and no pending items
  if (isOnline && pendingCount === 0 && !showBanner && !syncResult) {
    return null;
  }

  return (
    <>
      {/* Offline Banner */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white px-4 py-2 z-50 flex items-center justify-center gap-2 text-sm">
          <WifiOff className="w-4 h-4" />
          <span>Çevrimdışı mod - Değişiklikler otomatik kaydedilecek</span>
        </div>
      )}

      {/* Back Online Banner */}
      {isOnline && showBanner && (
        <div className="fixed top-0 left-0 right-0 bg-green-500 text-white px-4 py-2 z-50 flex items-center justify-center gap-2 text-sm animate-fade-in">
          <Wifi className="w-4 h-4" />
          <span>Bağlantı sağlandı - Veriler senkronize ediliyor...</span>
        </div>
      )}

      {/* Sync Result Toast */}
      {syncResult && (
        <div className="fixed top-4 right-4 bg-white shadow-lg rounded-lg px-4 py-3 z-50 flex items-center gap-2 animate-fade-in border">
          <Check className="w-5 h-5 text-green-500" />
          <span className="text-sm">
            {syncResult.synced} işlem senkronize edildi
            {syncResult.failed > 0 && `, ${syncResult.failed} başarısız`}
          </span>
        </div>
      )}

      {/* Pending Operations Badge - Fixed Bottom */}
      {pendingCount > 0 && isOnline && (
        <button
          onClick={handleManualSync}
          disabled={syncing}
          className="fixed bottom-20 right-4 bg-amber-500 text-white px-3 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm z-40 hover:bg-amber-600 transition-colors"
        >
          {syncing ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <CloudOff className="w-4 h-4" />
          )}
          <span>{pendingCount} bekleyen</span>
        </button>
      )}
    </>
  );
};

export default OfflineIndicator;
